import { applyKeyAliases } from '../shape-map/key-aliases';
import { classifyFieldValue } from './classify-field-value';

export function buildFields({ row, rowEntry, predecessor, predecessorEntry, resolved, predecessorStatus }) {
  const { normalized: currentNormalized } = applyKeyAliases(rowEntry, row.debugData ?? {});
  const excludedKeys = excludedKeysFor(rowEntry, resolved.identity);
  const fieldKeys = Object.keys(currentNormalized).filter(key => !excludedKeys.includes(key));

  if (resolved.renderMode === 'keyValue') {
    return fieldKeys.map(key => buildKeyValueField(key, rowEntry, currentNormalized));
  }

  // predecessor and predecessorEntry are independent: a predecessor row whose action has no map
  // entry arrives with entry null, and the downstream unknown/predecessorPruned path already
  // handles a null normalization.
  const predecessorNormalized =
    predecessor && predecessorEntry ? applyKeyAliases(predecessorEntry, predecessor.debugData ?? {}).normalized : null;

  return fieldKeys.map(key =>
    buildDiffOrComplexField({
      key,
      rowEntry,
      predecessorEntry,
      currentNormalized,
      predecessorNormalized,
      createsRecord: rowEntry.createsRecord,
      predecessorStatus,
    })
  );
}

function excludedKeysFor(rowEntry, identity) {
  if (identity.source === 'idKeys') return [identity.matchedAlias];
  // identity.recordId for a composite match is the array of resolved VALUES, not the key names
  // the field set needs to exclude — the key names live only on the entry.
  if (identity.source === 'compositeIdKeys') return rowEntry.compositeIdKeys;
  return [];
}

function buildKeyValueField(key, entry, currentNormalized) {
  const value = classifyFieldValue({
    hasRow: true,
    entry,
    normalizedData: currentNormalized,
    key,
  });
  return { key, label: key, complex: isComplexKey(entry, key), diffed: false, value };
}

function buildDiffOrComplexField({
  key,
  rowEntry,
  predecessorEntry,
  currentNormalized,
  predecessorNormalized,
  createsRecord,
  predecessorStatus,
}) {
  const next = classifyFieldValue({ hasRow: true, entry: rowEntry, normalizedData: currentNormalized, key });

  if (isComplexKey(rowEntry, key)) {
    return { key, label: key, complex: true, diffed: false, notDiffedReason: 'complexValue', current: next };
  }

  const previous = classifyFieldValue({
    hasRow: predecessorNormalized !== null,
    entry: predecessorEntry,
    normalizedData: predecessorNormalized ?? {},
    key,
    createsRecord,
    predecessorStatus,
  });

  return { key, label: key, complex: false, diffed: true, previous, next };
}

function isComplexKey(entry, key) {
  return (entry.complexKeys ?? []).includes(key);
}
